import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

/**
 * Simple Connectivity Table - Device connectivity status
 * 
 * Shows daily transmission counts and connectivity status for all devices.
 * NOW USING ORGANIZATION ENDPOINTS VIA SharedPerformanceCache
 */
const SimpleConnectivityTable = ({ selectedOrgId, startDate, endDate }) => {
  const [connectivityData, setConnectivityData] = useState({});
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo,
    isEndpointLoading
  } = useSharedPerformanceCache();

  useEffect(() => {
    loadConnectivityData();
  }, [selectedOrgId, startDate, endDate]);

  // Update data whenever cache changes
  useEffect(() => {
    const devices = getDevices();
    const dataMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('connection-count', device.id);
      if (data) {
        dataMap[device.id] = data;
      }
    });
    
    setConnectivityData(dataMap);
  }, [cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  const loadConnectivityData = () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    loadAllPerformanceData(selectedOrgId, startDate, endDate);
  };

  // Get devices from shared cache
  const devices = getDevices();

  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];


  return (
    <WalteroCard sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device Connectivity - Daily
        </WalteroTypography>
        {cacheInfo.isLoading && (
          <CircularProgress size={16} />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
Device connectivity status. Shows daily transmission counts.
      </Typography>
      

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Device
                </TableCell>
                {sortedDates.map((date) => (
                  <TableCell key={date} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: '80px', textAlign: 'center' }}>
                    {date.slice(-5)} {/* Show MM-DD */}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.sort((a, b) => {
                // Sort by device name/serial number
                const aName = String(a.serialnumber || a.name || a.id || '');
                const bName = String(b.serialnumber || b.name || b.id || '');
                return aName.localeCompare(bName);
              }).map((device) => {
                const deviceData = connectivityData[device.id];
                return (
                  <TableRow key={device.id} hover>
                    <TableCell 
                      component="th" 
                      scope="row" 
                      sx={{ 
                        fontWeight: 'medium',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                        title={device.serialnumber || device.name || device.id}
                      >
                        {device.serialnumber || device.name || device.id}
                      </Typography>
                    </TableCell>
                    {sortedDates.map((date) => {
                      const dayData = deviceData?.dailyConnectivity?.find(d => d.date === date);
                      const transmissionCount = dayData?.transmissionCount || 0;
                      const connected = dayData?.connected || false;

                      return (
                        <TableCell 
                          key={date} 
                          sx={{ 
                            textAlign: 'center',
                            backgroundColor: connected ? '#e8f5e8' : '#fdf3eb',
                            border: '1px solid #e0e0e0',
                            padding: '4px',
                            fontSize: '11px'
                          }}
                        >
                          {isEndpointLoading('connection-count', device.id) ? (
                            <CircularProgress size={12} />
                          ) : transmissionCount > 0 ? (
                            transmissionCount
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(connectivityData).length} with data, {sortedDates.length} days
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Daily connectivity data. Green cells indicate successful connections.
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default SimpleConnectivityTable;